import React, {useState} from "react"
import {Body} from "@irati/ui/src/Typography"
import Validation from "@irati/utils/validation"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import objectHash from "object-hash"
import {
  AutomationRewardType,
  CelebrationTag,
  RecognitionAutomationInput,
  RecognitionAutomationRule,
  RecognitionAutomationRuleInput,
  RecognitionAutomationRuleType
} from "@irati/types"
import adminTracking from "@irati-tracking/admin"
import {components} from "react-select"
import Flex from "@irati/ui/src/Flex"
import mainTheme from "@irati/ui/src/global/theme"
import {Input, Select} from "@irati/ui/src/FormItem"
import perkboxStyles from "@irati/ui/src/FormItem/styles/selectStyles"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Spacing from "@irati/ui/src/Spacing"
import {SelectOption} from "@irati/ui/src/FormItem/Select"
import Button, {IconButton} from "@irati/ui/src/Button"
import CardOption from "./CardOptions"
import {YearOptionContainer} from "./styled"
import styled from "styled-components"
import Message, {OnChangeProps} from "@irati-web/fields/recognition/Message"
import {festiveTemplateSchema} from "@irati-web/fields/poll/votes/validation"
import {Hr} from "@irati/ui/src/Layout"
import OccasionsSelect from "@irati-web/fields/occasions/OccasionsSelect"
import {CompanyValuesSelect} from "@irati-web/fields/companyValues"
const pointsPerUserSchema = () =>
  Validation.string("platform.defaultValidation.required").test(
    "Positive, value above 1 and no decimal",
    "platform.validation.number.nonZeroValue",
    (val) => val > 0
  )
const TEMPLATE_MESSAGE_MAX_LENGTH = 256
interface Props {
  automation: RecognitionAutomationInput
  celebrationTag: CelebrationTag
  onAutomationChange: (automation: RecognitionAutomationInput) => void
  onRuleTypeChange: (ruleType: RecognitionAutomationRuleType) => void
  onRulesChange: (rules: RecognitionAutomationRule[]) => void
  showPreview: () => void
}
type YearsSettingRow = {
  id: number
  years: number[]
  message?: string
  amount?: number
}
const CultureContainer = styled(Flex)`
  > * {
    flex: 1;
  }
  gap: ${mainTheme.spacing.oneAndHalf};
`
const StyledRowContainer = styled(Flex)`
  background-color: ${mainTheme.colors.greys.grey03};
  padding: ${mainTheme.spacing.two};
  border-radius: ${mainTheme.spacing.one};
  align-items: flex-start;
  gap: ${mainTheme.spacing.one};
  justify-content: space-between;
  width: 100%;
  #message__textarea {
    flex: 1;
    min-width: 200px;
  }
  .input-group {
    margin-top: 0;
  }
`
const StyledContainer = styled(Flex)`
  flex-direction: column;
  gap: ${mainTheme.spacing.oneAndHalf};
  border-radius: ${mainTheme.spacing.one};
  padding: ${mainTheme.spacing.two};
  border: 1px solid ${mainTheme.colors.greys.grey12};
`
const StyledOptionsContainer = styled(Flex)`
  border-radius: ${mainTheme.spacing.one};
  background-color: ${mainTheme.colors.greys.grey03};
  padding: ${mainTheme.spacing.half};
`
const EveryYearChipContainer = styled(Flex)`
  background-color: ${mainTheme.colors.greys.white};
  padding: ${mainTheme.spacing.one} ${mainTheme.spacing.two};
  border: 1px solid ${mainTheme.colors.greys.grey20};
`
const YearsArray = Array.from({length: 40}, (_, index) => index + 1)
const EveryYearChip = () => (
  <EveryYearChipContainer justifyContent="center" alignItems="center" gap={mainTheme.spacing.oneAndHalf}>
    <Icon icon={IconTypes.refresh} />
    <Body small weight="bold" color={mainTheme.colors.greys.grey50}>
      <FormattedMessage id="admin.automation.create.rules.everyYear" />
    </Body>
  </EveryYearChipContainer>
)
const mapSettingsToRules = (settings: YearsSettingRow[]): RecognitionAutomationRule[] => {
  return settings
    .map((setting) => {
      return setting.years.map<RecognitionAutomationRule>((year) => ({
        year,
        amount: parseInt(setting.amount),
        message: setting.message
      }))
    })
    .flat()
}
const mapRulesToSettings = (rules: RecognitionAutomationRuleInput[]): YearsSettingRow[] => {
  if (!rules?.length) {
    return [
      {
        years: [],
        id: 1
      }
    ]
  }
  const groupYearsPoints = rules.reduce(
    (acc, item, index) => {
      const key = objectHash({amount: item.amount, message: item.message || ""})
      const prev = acc[key] || {years: [], amount: item.amount, message: item.message}
      return {
        ...acc,
        [key]: {
          id: index,
          years: [...prev.years, item.year],
          amount: item.amount,
          message: item.message
        }
      }
    },
    {} as Record<string, YearsSettingRow>
  )
  return Object.values(groupYearsPoints)
}
const AutomationsRules = (props: Props) => {
  const {onRulesChange, celebrationTag, onRuleTypeChange, automation, onAutomationChange, showPreview} = props
  const automationRewardType = celebrationTag.settings?.rewardType
  const intl = useIntl()
  const {rewardRules: rules, ruleType} = automation
  const isYearly = ruleType === RecognitionAutomationRuleType.Yearly
  const isCustom = ruleType === RecognitionAutomationRuleType.Custom
  const [yearsSettings, setYearsSettings] = useState(mapRulesToSettings(rules))
  const handleTypeChange = (type: RecognitionAutomationRuleType) => {
    const hasCustomYears = yearsSettings?.some((setting) => setting.years.indexOf(0) < 0)
    const hasYearly = yearsSettings?.some((setting) => setting.years.indexOf(0) >= 0)
    const newYearsSettings = Array.from(yearsSettings)
    if (type === RecognitionAutomationRuleType.Yearly && !hasYearly) {
      newYearsSettings.push({
        id: yearsSettings.reduce((maxId, setting) => Math.max(maxId, setting.id || 0), 0) + 1,
        message: "",
        years: [0],
        amount: 0
      })
    }
    if (type === RecognitionAutomationRuleType.Custom && !hasCustomYears) {
      newYearsSettings.push({
        years: [],
        message: "",
        amount: 0,
        id: yearsSettings.reduce((maxId, setting) => Math.max(maxId, setting.id || 0), 0) + 1
      })
    }
    setYearsSettings(newYearsSettings)
    onRulesChange(mapSettingsToRules(newYearsSettings))
    onRuleTypeChange(type)
  }
  const handleYearAmount = (amount: string, block: number) => {
    const newRules = Array.from(yearsSettings).map((setting) => ({
      ...setting,
      amount: setting.id === block ? parseInt(amount) : setting.amount,
      id: setting.id
    }))
    setYearsSettings(newRules)
    onRulesChange(mapSettingsToRules(newRules))
  }
  const handleYearSelect = (years: SelectOption[], rowId: number) => {
    const newYears = years.map(({value}) => value)
    newYears.sort((a, b) => a - b)
    const newRules = Array.from(yearsSettings).map((setting) => ({
      ...setting,
      years: setting.id === rowId ? newYears : setting.years,
      id: setting.id
    }))
    setYearsSettings(newRules)
    onRulesChange(mapSettingsToRules(newRules))
  }
  const addYearsRow = (e) => {
    adminTracking.trackAddAutomationRule(celebrationTag?.name)
    e?.preventDefault()
    setYearsSettings([
      ...yearsSettings,
      {
        years: [],
        message: "",
        amount: 0,
        id: yearsSettings.reduce((maxId, setting) => Math.max(maxId, setting.id || 0), 0) + 1
      }
    ])
  }
  const removeRow = (rowId: number) => {
    const newYearsSettings = Array.from(yearsSettings).filter(({id}) => id !== rowId)
    setYearsSettings(newYearsSettings)
    onRulesChange(mapSettingsToRules(newYearsSettings))
  }
  const yearsSelect = YearsArray.filter((val) => !rules?.some((rule) => rule.year === val)).map((year) => ({
    label: intl.formatMessage({id: "admin.automation.lbl.customYears"}, {years: year}),
    value: year
  }))
  const onMessageChange = (data: OnChangeProps, rowId: number) => {
    const newRules = Array.from(yearsSettings).map((yearSetting) => ({
      ...yearSetting,
      message: yearSetting.id === rowId ? data.message : yearSetting.message,
      id: yearSetting.id
    }))
    setYearsSettings(newRules)
    onRulesChange(mapSettingsToRules(newRules))
  }
  const ContainerComponent = automationRewardType === AutomationRewardType.FixedCustom ? StyledContainer : Flex
  const yearlyReward = yearsSettings.find((setting) => setting.years.indexOf(0) >= 0)
  return (
    <ContainerComponent marginTop={3} direction="column" gap={mainTheme.spacing.one}>
      {automationRewardType === AutomationRewardType.FixedCustom && (
        <StyledOptionsContainer>
          <CardOption full selected={isYearly} onSelect={() => handleTypeChange(RecognitionAutomationRuleType.Yearly)}>
            <Body small weight={isYearly ? "light" : "semibold"}>
              <FormattedMessage id="admin.automation.create.rules.title.yearlyReward" />
            </Body>
          </CardOption>
          <CardOption full selected={isCustom} onSelect={() => handleTypeChange(RecognitionAutomationRuleType.Custom)}>
            <Body small weight={isCustom ? "light" : "semibold"}>
              <FormattedMessage id="admin.automation.create.rules.title.customYears" />
            </Body>
          </CardOption>
        </StyledOptionsContainer>
      )}
      {isYearly && (
        <StyledRowContainer>
          <EveryYearChip />
          <Message
            hideLabel
            placeholder="admin.automation.create.message.placeholder"
            name="messageBox"
            hideExtraActions
            defaultValues={{
              message: yearlyReward?.message || ""
            }}
            messageLength={TEMPLATE_MESSAGE_MAX_LENGTH}
            onChange={(data) => onMessageChange(data, yearlyReward?.id)}
            validation={festiveTemplateSchema(intl)}
          />
          <Input
            id="pointsPerUser"
            name="pointsPerUser"
            type="number"
            min="0"
            addon={{
              before: <Icon icon={IconTypes.rewardPoints} />
            }}
            flush
            validation={yearlyReward?.amount ? pointsPerUserSchema() : null}
            value={String(yearlyReward?.amount)}
            placeholder="platform.automation.distribute.enterPoints.placeholder"
            onChange={(val) => handleYearAmount(val, yearlyReward?.id)}
          />
        </StyledRowContainer>
      )}
      {isCustom && (
        <Spacing>
          {yearsSettings
            .filter(({years}) => years.indexOf(0) < 0)
            .map(({amount, years, message, id}) => (
              <StyledRowContainer key={id} marginBottom={1}>
                <YearOptionContainer gap={1} alignItems="flex-start" flex="1">
                  <Select
                    isClearable
                    isMulti
                    flush
                    value={years.map((yearOption) => ({
                      value: yearOption,
                      label: intl.formatMessage({id: "admin.automation.lbl.customYears"}, {years: yearOption})
                    }))}
                    styles={{
                      ...perkboxStyles,
                      container: (base) => ({
                        ...base,
                        marginBottom: mainTheme.spacing.one
                      })
                    }}
                    options={yearsSelect}
                    validation={Validation.string(intl.formatMessage({id: "platform.defaultValidation.required"}))}
                    onChange={(val: SelectOption[]) => handleYearSelect(val, id)}
                  />
                  <Message
                    hideLabel
                    placeholder="admin.automation.create.message.placeholder"
                    name="messageBox"
                    hideExtraActions
                    defaultValues={{
                      message: message || ""
                    }}
                    messageLength={TEMPLATE_MESSAGE_MAX_LENGTH}
                    onChange={(data) => onMessageChange(data, id)}
                    validation={festiveTemplateSchema(intl)}
                  />
                  <Input
                    type="number"
                    min="0"
                    disabled={!years.length}
                    addon={{
                      before: <Icon icon={IconTypes.rewardPoints} />
                    }}
                    flush
                    validation={amount ? pointsPerUserSchema() : null}
                    value={String(amount)}
                    placeholder="platform.automation.distribute.enterPoints.placeholder"
                    onChange={(val) => handleYearAmount(val, id)}
                  />
                </YearOptionContainer>
                {yearsSettings.length > 1 && (
                  <IconButton
                    large
                    color={mainTheme.colors.greys.grey50}
                    icon={IconTypes.delete}
                    onClick={() => removeRow(id)}></IconButton>
                )}
              </StyledRowContainer>
            ))}
          <Flex justifyContent="center">
            <Button onClick={addYearsRow} small>
              <Flex gap={1} alignItems="center">
                <FormattedMessage id="admin.automation.cta.addNewRule" />
                <Icon icon={IconTypes.plus} small />
              </Flex>
            </Button>
          </Flex>
        </Spacing>
      )}
      <Flex direction="column" gap={mainTheme.spacing.oneAndHalf}>
        <Hr />
        <Body>
          <FormattedMessage id="admin.automation.create.rules.companyCulture" />
        </Body>
        <CultureContainer>
          <OccasionsSelect
            hideLabel
            selectedOption={automation.companyCelebrationId}
            placeholder="admin.automation.create.rules.selectOccasion"
            onChange={(occasionId) =>
              onAutomationChange({
                ...automation,
                companyCelebrationId: occasionId
              })
            }
            components={{
              Placeholder: (componentProps) => (
                <components.Placeholder {...componentProps}>
                  <Flex gap={1} alignItems="center">
                    <Icon icon={IconTypes.celebration} />
                    {componentProps.children}
                  </Flex>
                </components.Placeholder>
              )
            }}
          />
          <CompanyValuesSelect
            hideLabel
            item={{companyValues: automation.companyValueIds}}
            placeholder="admin.automation.create.rules.selectValues"
            components={{
              Placeholder: (componentProps) => (
                <components.Placeholder {...componentProps}>
                  <Flex gap={1} alignItems="center">
                    <Icon icon={IconTypes.star} />
                    {componentProps.children}
                  </Flex>
                </components.Placeholder>
              )
            }}
            onChange={(_, key, value) =>
              onAutomationChange({
                ...automation,
                companyValueIds: value
              })
            }
          />
        </CultureContainer>

        <Flex marginTop={2}>
          <Button cta onClick={showPreview}>
            <FormattedMessage id="admin.automation.create.viewPreview.cta" />
          </Button>
        </Flex>
      </Flex>
    </ContainerComponent>
  )
}

export default AutomationsRules
